import type { DismissedNotificationEntry, NotificationFeedStatus } from "@/src/shared/contracts/app";
import { feedRowStateKey, isFeedRowPresentation } from "./feedPresentation";

export const NOTIFICATION_FEED_REFRESH_EVENT = "nvzero:notification-feed-refresh";

type NotificationFeedSession = {
  userId?: unknown;
  isAuthorized?: unknown;
} | null | undefined;

export function isNotificationFeedRequestCurrent(args: {
  requestGeneration: number;
  currentGeneration: number;
  requestedUserId: string;
  currentSession: NotificationFeedSession;
}): boolean {
  return Boolean(
    args.requestedUserId
    && args.requestGeneration === args.currentGeneration
    && args.currentSession?.isAuthorized === true
    && args.requestedUserId === String(args.currentSession.userId || ""),
  );
}

export function shouldAutoOpenNotificationFeed(args: {
  visibleCount: number;
  isInitialFeed: boolean;
  newEntryCount: number;
}): boolean {
  return !args.isInitialFeed && args.visibleCount > 0 && args.newEntryCount > 0;
}

export type NotificationFeedWindowProps = { title: string; view: "now" | "updates" };

/** Restore saved notification windows; only new arrivals create a new window. */
export function materializeNotificationFeedWindow<T>(args: {
  isInitialFeed: boolean;
  windows: Record<string, readonly { id?: string; name?: string }[]>;
  savedComponents?: unknown;
  initialWindowDismissed?: boolean;
  restoreSaved?: (saved: Record<string, unknown>) => T | null;
  create: (props: NotificationFeedWindowProps) => T | null;
  add: (component: T) => void;
}): "created" | "existing" | "unavailable" | "dismissed" | "idle" {
  const matches = (component: { id?: string; name?: string } | null) => component?.id === "notifications" || component?.name === "Notifications";
  if (Object.values(args.windows).some((components) => components.some(matches))) return "existing";
  let saved = args.savedComponents;
  if (typeof saved === "string") {
    try { saved = JSON.parse(saved); } catch { saved = []; }
  }
  const savedWindow = Array.isArray(saved) ? saved.find(matches) : undefined;
  if (savedWindow && !args.restoreSaved) return "existing";
  if (!savedWindow && args.isInitialFeed && args.initialWindowDismissed) return "dismissed";
  if (!savedWindow && args.isInitialFeed) return "idle";
  const component = savedWindow && args.restoreSaved
    ? args.restoreSaved(savedWindow)
    : args.create({ title: "Notifications", view: "updates" });
  if (!component) return "unavailable";
  args.add(component);
  return "created";
}

/** Shared restoration defaults for the desktop grid and mobile startup. */
export function hydrateNotificationWindow(component: Record<string, unknown>) {
  const id = typeof component.id === "string" && component.id ? component.id : "notifications";
  const props = component.props && typeof component.props === "object" && !Array.isArray(component.props)
    ? component.props as Record<string, unknown> : {};
  const size = component.size as { width?: unknown; height?: unknown } | undefined;
  const hasSize = typeof size?.width === "number" && Number.isFinite(size.width) && size.width > 0
    && typeof size.height === "number" && Number.isFinite(size.height) && size.height > 0;
  return {
    ...component, id, name: "Notifications", priority: component.priority || "Mid",
    size: hasSize ? size : { width: 430, height: 320 }, group: component.group || "Notifications",
    score: typeof component.score === "number" ? component.score : 4,
    createdDate: component.createdDate ?? Date.now(), reason: component.reason || "Notifications",
    props: { ...props, id, title: props.title || "Notifications" },
  };
}

export const NOTIFICATION_FEED_PENDING: NotificationFeedStatus = Object.freeze({ state: "pending" });
const NOTIFICATION_FEED_READY: NotificationFeedStatus = Object.freeze({ state: "ready" });

/**
 * The feed status after one read answered or failed. A failure remembers
 * whether any read answered before it. Returns `current` itself when nothing
 * changed, so a steady poll does not touch the store.
 */
export function nextNotificationFeedStatus(
  current: NotificationFeedStatus,
  outcome: "ready" | "error",
): NotificationFeedStatus {
  if (outcome === "ready") return current.state === "ready" ? current : NOTIFICATION_FEED_READY;
  const answered = current.state === "ready" || (current.state === "error" && current.answered);
  return current.state === "error" && current.answered === answered ? current : { state: "error", answered };
}

/**
 * What a feed with no rows to show says. Before the feed has answered, an
 * empty list is unknown: it reads as loading, or as nothing at all once the
 * read failed (the header carries that error), and never as "caught up".
 * Rows already on screen were read from somewhere, so they count as an answer.
 */
export type EmptyNotificationFeedState = "loading" | "unavailable" | "filtered" | "caught-up";

export function emptyNotificationFeedState(args: {
  status: NotificationFeedStatus;
  hasRows: boolean;
  filtering: boolean;
}): EmptyNotificationFeedState {
  const { status } = args;
  const answered = args.hasRows || status.state === "ready" || (status.state === "error" && status.answered);
  if (!answered) return status.state === "error" ? "unavailable" : "loading";
  return args.filtering ? "filtered" : "caught-up";
}

const CHANNEL_NAME = "nvzero-notification-feed-sync";

export function requestNotificationFeedRefresh(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(NOTIFICATION_FEED_REFRESH_EVENT));
}

/** How long {@link readNotificationFeedNow} waits for the feed before giving up. */
export const NOTIFICATION_FEED_READ_WAIT_MS = 4_000;

const feedReadWaiters = new Set<(read: boolean) => void>();

/**
 * The feed's owner reports each read it publishes (`true`) or fails (`false`).
 * Only the newest read reports: a refresh supersedes any read in flight.
 */
export function settleNotificationFeedRead(read: boolean): void {
  const waiters = [...feedReadWaiters];
  feedReadWaiters.clear();
  for (const waiter of waiters) waiter(read);
}

/**
 * Reads the feed now and resolves once that read is in the store: `true`
 * when it was published, `false` when it failed, no feed is mounted, or it
 * took longer than `waitMs`. For a decision that needs rows the 60-second
 * poll may not have read yet.
 */
export function readNotificationFeedNow(waitMs = NOTIFICATION_FEED_READ_WAIT_MS): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  return new Promise((resolve) => {
    const finish = (read: boolean) => {
      clearTimeout(timer);
      feedReadWaiters.delete(finish);
      resolve(read);
    };
    const timer = setTimeout(() => finish(false), waitMs);
    feedReadWaiters.add(finish);
    requestNotificationFeedRefresh();
  });
}

export function broadcastNotificationFeed(userId: string, notifications: unknown[]): void {
  if (typeof BroadcastChannel === "undefined" || !userId) return;
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ userId, notifications, timestamp: Date.now() });
  channel.close();
}

export function onNotificationFeedBroadcast(
  userId: string,
  callback: (notifications: unknown[]) => void,
): () => void {
  if (typeof BroadcastChannel === "undefined" || !userId) return () => {};
  const channel = new BroadcastChannel(CHANNEL_NAME);
  const handler = (event: MessageEvent) => {
    if (event.data?.userId !== userId || !Array.isArray(event.data?.notifications)) return;
    callback(event.data.notifications);
  };
  channel.addEventListener("message", handler);
  return () => {
    channel.removeEventListener("message", handler);
    channel.close();
  };
}

/**
 * The key a dismissal of a stored feed row is recorded under. A row the
 * server presented (feed contract v1) is keyed by its row id and revision, so
 * a dismissed row comes back once its content changes; an older row by its
 * stored id.
 */
export function storedFeedRowKey(entry: unknown, index: number): string {
  const value = entry && typeof entry === "object" ? entry as Record<string, unknown> : {};
  if (isFeedRowPresentation(value.presentation)) return feedRowStateKey(value.presentation);
  return String(
    value.id
      || value._id
      || value._key
      || `${value.vendor ?? "system"}-${value.storedDate ?? value.createdDate ?? index}`,
  );
}

/** Splits the rows Done hides (by `storedFeedRowKey`) from the rest, keeping each one's place. */
export function takeDoneEntries<T>(entries: readonly T[], keys: ReadonlySet<string>): { remaining: T[]; removed: DismissedNotificationEntry[] } {
  const remaining: T[] = [];
  const removed: DismissedNotificationEntry[] = [];
  entries.forEach((entry, index) => {
    const key = storedFeedRowKey(entry, index);
    if (keys.has(key)) removed.push({ key, index, entry });
    else remaining.push(entry);
  });
  return { remaining, removed };
}

/**
 * Puts back rows whose Done the server did not record, each at its old
 * place (clamped to the list). A row the list already holds again, for
 * example from a feed read since, is not added twice.
 */
export function restoreDoneEntries<T>(entries: readonly T[], removed: readonly DismissedNotificationEntry[]): T[] {
  const present = new Set(entries.map((entry, index) => storedFeedRowKey(entry, index)));
  const next = [...entries];
  for (const item of [...removed].sort((a, b) => a.index - b.index)) {
    if (present.has(item.key)) continue;
    present.add(item.key);
    next.splice(Math.min(item.index, next.length), 0, item.entry as T);
  }
  return next;
}

/**
 * Rows marked done that the feed may still return, by `storedFeedRowKey`:
 * 0 while the write is in flight, else the time the server confirmed it.
 * Held in memory only; the durable copy is the server's feed state.
 */
export type PendingDoneNotificationKeys = Readonly<Record<string, number>>;

/**
 * The pending done keys a feed read started at `requestStartedAt` leaves
 * pending. A key the server confirmed before that read began is in the
 * server's state, so that read and every later one already leave the row
 * out. A key still in flight, or confirmed after the read began, keeps
 * hiding the row, since that read may predate the write.
 */
export function pendingDoneAfterFeedRead(pending: PendingDoneNotificationKeys, requestStartedAt: number): Record<string, number> {
  return Object.fromEntries(Object.entries(pending).filter(([, confirmedAt]) => confirmedAt === 0 || confirmedAt >= requestStartedAt));
}

/**
 * Rows taken back with Undo, by `storedFeedRowKey`: 0 while the `reopen`
 * write is in flight, else the time the server confirmed it. Held in memory
 * only. A feed read that may predate the reopen still leaves these rows out
 * as done, so the list keeps its own copy of them until a later read serves
 * them. The keys expire the way pending done keys do
 * (`pendingDoneAfterFeedRead`).
 */
export type PendingReopenNotificationKeys = Readonly<Record<string, number>>;

/**
 * Keeps each held row (`PendingReopenNotificationKeys`) that a feed answer
 * leaves out, at its place in the `current` list. A row the answer serves
 * again is not added twice.
 */
export function holdReopenedEntries<T>(entries: readonly T[], current: readonly T[], held: readonly string[]): T[] {
  if (held.length === 0) return [...entries];
  return restoreDoneEntries(entries, takeDoneEntries(current, new Set(held)).removed);
}

/**
 * The optimistic read keys a feed answer leaves to show: those whose row is
 * still in the feed and not yet read there. Once the server reports a row
 * read, or the row is gone, its local key has nothing left to do.
 */
export function readKeysAfterFeedRead(readKeys: readonly string[], entries: readonly unknown[]): string[] {
  const unread = new Set<string>();
  entries.forEach((entry, index) => {
    const presentation = entry && typeof entry === "object" ? (entry as Record<string, unknown>).presentation : undefined;
    if (isFeedRowPresentation(presentation) && presentation.state?.read === true) return;
    unread.add(storedFeedRowKey(entry, index));
  });
  return readKeys.filter((key) => unread.has(key));
}

/** Email extraction is bookkeeping within a sync, not a separate user event.
 * Source identity keeps meeting recaps, lifecycle changes and failures independent. */
export function isUsefulNotificationFeedEntry(entry: unknown): boolean {
  return !(entry && typeof entry === "object" && "source" in entry
    && entry.source === "email_extraction");
}

export function filterDismissedNotificationFeedEntries<T>(
  notifications: readonly T[],
  dismissedKeys: readonly string[],
): T[] {
  if (!Array.isArray(notifications) || notifications.length === 0) return [];
  const dismissed = new Set((dismissedKeys || []).map(String));
  return notifications.filter(
    (notification, index) => isUsefulNotificationFeedEntry(notification)
      && !dismissed.has(storedFeedRowKey(notification, index)),
  );
}
